import React, { useState, useContext, useRef, useEffect } from 'react';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Slider } from 'primereact/slider';
import { useWeb3React } from '@web3-react/core';
import ContractContext from '../../ContractContext';
import TokenInput from './TokenInput';
import TokenSelectorModal from './TokenSelectorModal';
import { Toast } from 'primereact/toast';
import { getTokenLogo } from '../../utils';

// Define TransferType options
const transferTypeOptions = [
    { label: 'Sell', value: 0 },
    { label: 'Buy', value: 1 },
    { label: 'Transfer', value: 2 },
    { label: 'Stake', value: 3 },
    { label: 'Burn', value: 4 },
    { label: 'Unstake', value: 5 },
    { label: 'Fund', value: 6 },
    { label: 'Add Liquidity', value: 7 },
    { label: 'Remove Liquidity', value: 8 },
    { label: 'Stake LP', value: 9 },
    { label: 'Unstake LP', value: 10 }
];

const ControlDeck = ({ tokenSelections, refreshTokenSelections, imageReferences }) => {
    const { account } = useWeb3React();
    const contractContext = useContext(ContractContext);
    const contracts = contractContext.contracts;

    // States for various fields
    const [selectedToken, setSelectedToken] = useState(null);
    const [showTokenSelector, setShowTokenSelector] = useState(false);
    const [transferType, setTransferType] = useState('');
    const [fee, setFee] = useState(0);
    const [threshold, setThreshold] = useState(0);
    const [recipientAddress, setRecipientAddress] = useState('');
    const [stakeRewards, setStakeRewards] = useState(false);
    const [allowRocketPlay, setAllowRocketPlay] = useState(false);
    const [cooldownTime, setCooldownTime] = useState(0);
    const [cooldownHolder, setCooldownHolder] = useState('');
    const [cooldownTransferType, setCooldownTransferType] = useState('');
    const [feeDistribution, setFeeDistribution] = useState({
        otherTeam: 0,
        rocketPlay: 0,
        staking: 0,
        burnToEarn: 0,
        fundToEarn: 0,
    });
    const [ethFeeDistribution, setETHFeeDistribution] = useState({
        otherTeam: 0,
        rewardPackAll: 0,
        rewardPack: 0,
    });
    const [tokenomics, setTokenomics] = useState(null);
    const [feeDistributionData, setFeeDistributionData] = useState(null);
    const [cooldownActive, setCooldownActive] = useState(null);

    const toast = useRef(null);

    // Fetch tokenomics and fee distribution when token is selected
    useEffect(() => {
        if (selectedToken) {
            fetchTokenomics(selectedToken.address).then(setTokenomics);
            fetchFeeDistribution(selectedToken.address).then(setFeeDistributionData);
        }
    }, [selectedToken]);

    const handleTokenSelect = (token) => {
        setSelectedToken(token);
        setShowTokenSelector(false);
    };

    const fetchTokenomics = async (tokenAddress) => {
        try {
            const tokenomics = await contracts.floatieGatewayOracle.methods.getAllTokenomics(tokenAddress).call();
            return tokenomics;
        } catch (error) {
            console.error('Error fetching tokenomics:', error);
            return null;
        }
    };

    const fetchFeeDistribution = async (tokenAddress) => {
        try {
            const feeDistribution = await contracts.floatieGatewayOracle.methods.getFeeDistribution(tokenAddress).call();
            return feeDistribution;
        } catch (error) {
            console.error('Error fetching fee distribution:', error);
            return null;
        }
    };

    const fetchCooldown = async (tokenAddress, holderAddress, transferType) => {
        try {
            const cooldownActive = await contracts.floatieGateway.methods.checkCooldown(tokenAddress, holderAddress, transferType).call();
            return cooldownActive;
        } catch (error) {
            console.error('Error fetching cooldown:', error);
            return null;
        }
    };

    const handleSetTokenomics = async () => {
        if (!selectedToken || transferType === '' || !fee) {
            toast.current.show({ severity: 'error', summary: 'Incomplete Input', detail: 'Token, TransferType, and Fee are required.' });
            return;
        }

        try {
            await contracts.floatieGateway.methods
                .setTokenomics(selectedToken.address, transferType, fee * 100) // Scaling fee back to base unit
                .send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Tokenomics set successfully' });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.message });
        }
    };

    const handleSetThreshold = async () => {
        try {
            await contracts.floatieGateway.methods.setThreshold(threshold).send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Threshold set successfully' });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.message });
        }
    };

    const handleWithdrawMarketingFees = async () => {
        if (!selectedToken || !recipientAddress) {
            toast.current.show({ severity: 'error', summary: 'Incomplete Input', detail: 'Token and Recipient Address are required.' });
            return;
        }

        try {
            await contracts.floatieGateway.methods
                .withdrawMarketingFees(selectedToken.address, recipientAddress, stakeRewards)
                .send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Marketing fees withdrawn successfully' });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.message });
        }
    };

    const handleWithdrawOtherTeamFees = async () => {
        if (!selectedToken || !recipientAddress) {
            toast.current.show({ severity: 'error', summary: 'Incomplete Input', detail: 'Token and Recipient Address are required.' });
            return;
        }

        try {
            await contracts.floatieGateway.methods
                .withdrawOtherTeamFees(selectedToken.address, recipientAddress, stakeRewards)
                .send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Other team fees withdrawn successfully' });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.message });
        }
    };

    const handleFeeDistributionChange = (field, value) => {
        let remaining = 9000 - value; // Since RocketFi is locked at 1000 (10%)
        const updatedDistribution = { ...feeDistribution, [field]: value };

        const total = Object.values(updatedDistribution).reduce((acc, val) => acc + val, 0);
        if (total > 9000) {
            remaining = 9000;
        }

        setFeeDistribution({ ...updatedDistribution });
    };

    const handleSetFeeDistribution = async () => {
        if (!selectedToken) {
            toast.current.show({ severity: 'error', summary: 'Incomplete Input', detail: 'Token is required.' });
            return;
        }

        const totalFees = Object.values(feeDistribution).reduce((acc, val) => acc + val, 0);
        if (totalFees !== 9000) {
            toast.current.show({ severity: 'error', summary: 'Invalid Fee Distribution', detail: 'Total fees must add up to 90% (9000)' });
            return;
        }

        try {
            await contracts.floatieGateway.methods
                .setFeeDistribution(
                    selectedToken.address,
                    feeDistribution.otherTeam,
                    1000, // RocketFi Operations locked at 10%
                    feeDistribution.rocketPlay,
                    feeDistribution.staking,
                    feeDistribution.burnToEarn,
                    feeDistribution.fundToEarn
                )
                .send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Fee distribution set successfully' });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.message });
        }
    };

    const handleSetETHFeeDistribution = async () => {
        if (!selectedToken) {
            toast.current.show({ severity: 'error', summary: 'Incomplete Input', detail: 'Token is required.' });
            return;
        }

        const totalETHFees = Object.values(ethFeeDistribution).reduce((acc, val) => acc + val, 0);
        if (totalETHFees !== 10000) {
            toast.current.show({ severity: 'error', summary: 'Invalid ETH Fee Distribution', detail: 'Total ETH fees must add up to 100% (10000)' });
            return;
        }

        try {
            await contracts.floatieGateway.methods
                .setETHFeeDistribution(
                    selectedToken.address,
                    ethFeeDistribution.otherTeam,
                    ethFeeDistribution.rewardPackAll,
                    ethFeeDistribution.rewardPack
                )
                .send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'ETH Fee distribution set successfully' });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.message });
        }
    };

    const handleSetAllowRocketPlay = async () => {
        if (!selectedToken) {
            toast.current.show({ severity: 'error', summary: 'Incomplete Input', detail: 'Token is required.' });
            return;
        }

        try {
            await contracts.floatieGateway.methods
                .setAllowRocketPlayOnSell(selectedToken.address, allowRocketPlay)
                .send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Allow RocketPlay set successfully' });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.message });
        }
    };

    const handleSetCooldown = async () => {
        if (!selectedToken || !cooldownHolder || !cooldownTransferType || cooldownTime === 0) {
            toast.current.show({ severity: 'error', summary: 'Incomplete Input', detail: 'All fields are required for setting cooldown.' });
            return;
        }

        try {
            await contracts.floatieGateway.methods
                .setCooldown(selectedToken.address, cooldownHolder, cooldownTransferType, cooldownTime)
                .send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Cooldown set successfully' });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.message });
        }
    };

    const getTokenLogoUrl = (token) => getTokenLogo(imageReferences, token);

    return (
        <div style={{ padding: '1rem', maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ marginBottom: '1rem' }}>Control Deck</h2>

            {/* Section: Tokenomics */}
            <div style={{ marginBottom: '2rem' }}>
                <h3>Set Tokenomics</h3>
                <TokenInput
                    onSelectToken={() => setShowTokenSelector(true)}
                    token={selectedToken}
                    balance={0}
                    amountIn={0}
                    getTokenLogoUrl={getTokenLogoUrl}
                />
                <div style={{ marginTop: '1rem' }}>
                    <label>Transfer Type</label>
                    <Dropdown value={transferType} options={transferTypeOptions} onChange={(e) => setTransferType(e.value)} />
                </div>
                <div style={{ marginTop: '1rem' }}>
                    <label>Fee (%)</label>
                    <InputNumber value={fee} onValueChange={(e) => setFee(e.value)} mode="decimal" showButtons min={0} max={100} />
                </div>
                <Button label="Set Tokenomics" onClick={handleSetTokenomics} style={{ marginTop: '1rem' }} />

                {/* Display current tokenomics */}
                {tokenomics && (
                    <div style={{ marginTop: '1rem' }}>
                        <h4>Current Tokenomics</h4>
                        <p>Sell: {tokenomics.sell / 100}%</p>
                        <p>Buy: {tokenomics.buy / 100}%</p>
                        <p>Transfer: {tokenomics.transfer / 100}%</p>
                        <p>Stake: {tokenomics.stake / 100}%</p>
                        <p>Burn: {tokenomics.burn / 100}%</p>
                        <p>Unstake: {tokenomics.unstake / 100}%</p>
                        <p>Fund: {tokenomics.fund / 100}%</p>
                    </div>
                )}
            </div>

            {/* Section: Threshold */}
            <div style={{ marginBottom: '2rem' }}>
                <h3>Set Threshold</h3>
                <InputNumber value={threshold} onValueChange={(e) => setThreshold(e.value)} mode="decimal" showButtons min={0} />
                <Button label="Set Threshold" onClick={handleSetThreshold} style={{ marginTop: '1rem' }} />
            </div>

            {/* Section: Withdraw Fees */}
            <div style={{ marginBottom: '2rem' }}>
                <h3>Withdraw Marketing Fees</h3>
                <TokenInput
                    onSelectToken={() => setShowTokenSelector(true)}
                    token={selectedToken}
                    balance={0}
                    amountIn={0}
                    getTokenLogoUrl={getTokenLogoUrl}
                />
                <InputText value={recipientAddress} onChange={(e) => setRecipientAddress(e.target.value)} placeholder="Recipient Address" style={{ marginTop: '1rem' }} />
                <Checkbox checked={stakeRewards} onChange={(e) => setStakeRewards(e.checked)} style={{ marginTop: '1rem' }} />
                <Button label="Withdraw Marketing Fees" onClick={handleWithdrawMarketingFees} style={{ marginTop: '1rem' }} />
            </div>

            <div style={{ marginBottom: '2rem' }}>
                <h3>Withdraw Other Team Fees</h3>
                <TokenInput
                    onSelectToken={() => setShowTokenSelector(true)}
                    token={selectedToken}
                    balance={0}
                    amountIn={0}
                    getTokenLogoUrl={getTokenLogoUrl}
                />
                <InputText value={recipientAddress} onChange={(e) => setRecipientAddress(e.target.value)} placeholder="Recipient Address" style={{ marginTop: '1rem' }} />
                <Checkbox checked={stakeRewards} onChange={(e) => setStakeRewards(e.checked)} style={{ marginTop: '1rem' }} />
                <Button label="Withdraw Other Team Fees" onClick={handleWithdrawOtherTeamFees} style={{ marginTop: '1rem' }} />
            </div>

            {/* Section: Fee Distribution */}
            <div style={{ marginBottom: '2rem' }}>
                <h3>Set Fee Distribution</h3>
                {Object.keys(feeDistribution).map((key) => (
                    <div key={key} style={{ marginTop: '1rem' }}>
                        <label>{key}</label>
                        <Slider
                            value={feeDistribution[key]}
                            onChange={(e) => handleFeeDistributionChange(key, e.value)}
                            min={0}
                            max={9000} // Limiting max to 90% as RocketFi is locked at 10%
                            step={100} // Step by 1%
                        />
                        <p>{feeDistribution[key] / 100}%</p>
                    </div>
                ))}
                <Button label="Set Fee Distribution" onClick={handleSetFeeDistribution} style={{ marginTop: '1rem' }} />

                {/* Display current Fee Distribution */}
                {feeDistributionData && (
                    <div style={{ marginTop: '1rem' }}>
                        <h4>Current Fee Distribution</h4>
                        <p>{selectedToken.label} Project Operations: {feeDistributionData.otherTeam / 100}%</p>
                        <p>RocketFi Operations (Locked): 10%</p>
                        <p>Rocket Play: {feeDistributionData.rocketPlay / 100}%</p>
                        <p>Staking: {feeDistributionData.staking / 100}%</p>
                        <p>Burn to Earn: {feeDistributionData.burnToEarn / 100}%</p>
                        <p>Fund to Earn: {feeDistributionData.fundToEarn / 100}%</p>
                    </div>
                )}
            </div>

            {/* Section: ETH Fee Distribution */}
            <div style={{ marginBottom: '2rem' }}>
                <h3>Set ETH Fee Distribution</h3>
                {Object.keys(ethFeeDistribution).map((key) => (
                    <div key={key} style={{ marginTop: '1rem' }}>
                        <label>{key}</label>
                        <Slider
                            value={ethFeeDistribution[key]}
                            onChange={(e) => setETHFeeDistribution({ ...ethFeeDistribution, [key]: e.value })}
                            min={0}
                            max={10000} // Total must add up to 10000 (100%)
                            step={100} // Step by 1%
                        />
                        <p>{ethFeeDistribution[key] / 100}%</p>
                    </div>
                ))}
                <Button label="Set ETH Fee Distribution" onClick={handleSetETHFeeDistribution} style={{ marginTop: '1rem' }} />
            </div>

            {/* Section: RocketPlay Sell Enable */}
            <div style={{ marginBottom: '2rem' }}>
                <h3>Allow RocketPlay On Sell</h3>
                <Checkbox checked={allowRocketPlay} onChange={(e) => setAllowRocketPlay(e.checked)} />
                <Button label="Set RocketPlay Allowance" onClick={handleSetAllowRocketPlay} style={{ marginTop: '1rem' }} />
            </div>

            {/* Section: Cooldown */}
            <div style={{ marginBottom: '2rem' }}>
                <h3>Set Cooldown</h3>
                <InputText value={cooldownHolder} onChange={(e) => setCooldownHolder(e.target.value)} placeholder="Holder Address" style={{ marginTop: '1rem' }} />
                <Dropdown value={cooldownTransferType} options={transferTypeOptions} onChange={(e) => setCooldownTransferType(e.value)} />
                <InputNumber value={cooldownTime} onValueChange={(e) => setCooldownTime(e.value)} mode="decimal" showButtons min={0} style={{ marginTop: '1rem' }} />
                <Button label="Set Cooldown" onClick={handleSetCooldown} style={{ marginTop: '1rem' }} />
            </div>

            {/* TokenSelectorModal */}
            {showTokenSelector && (
                <TokenSelectorModal
                    tokens={tokenSelections}
                    onSelect={handleTokenSelect}
                    onClose={() => setShowTokenSelector(false)}
                    isVisible={showTokenSelector}
                    getTokenLogoUrl={getTokenLogoUrl}
                    setShowModal={setShowTokenSelector}
                />
            )}
            <Toast ref={toast} />
        </div>
    );
};

export default ControlDeck;
